import React from "react";
import { useTranslation } from "react-i18next";
import { Chip, StatusGlyph } from "@outpave/ds";
import type { ChipTone } from "@outpave/ds";
import CardStatusType from "enums/CardStatusType";
import { CardEnrollment } from "./types";

type BadgeContent = {
  tone: ChipTone;
  labelKey: string;
};

const ENROLLMENT_BADGES: Record<
  Exclude<CardEnrollment, "enrolled">,
  BadgeContent
> = {
  unassigned: { tone: "warning", labelKey: "Unassigned" },
  unenrolled: { tone: "neutral", labelKey: "Unenrolled" },
};

const readBadge = (
  enrollment: CardEnrollment,
  status: string,
): BadgeContent => {
  if (enrollment !== "enrolled") return ENROLLMENT_BADGES[enrollment];
  // An enrolled card is either importing spend or it is not.
  return status === CardStatusType.ACTIVE
    ? { tone: "positive", labelKey: "Active" }
    : { tone: "neutral", labelKey: "Paused" };
};

export interface CardStatusBadgeProps {
  enrollment: CardEnrollment;
  /** CardStatusType value, compared as a string so the view stays enum-free. */
  status: string;
}

/** A card's tab, and for an enrolled card whether its spend is importing. */
const CardStatusBadge: React.FC<CardStatusBadgeProps> = ({
  enrollment,
  status,
}) => {
  const { t } = useTranslation();
  const { tone, labelKey } = readBadge(enrollment, status);

  return (
    <Chip
      tone={tone}
      size="sm"
      leading={<StatusGlyph status={labelKey} tone={tone} />}
    >
      {t(labelKey)}
    </Chip>
  );
};

export default CardStatusBadge;
